import React from 'react';
import { CheckCircle2, Award, Target, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { aboutData } from '../mock';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We maintain the highest ethical standards in every appraisal',
    },
    {
      icon: Target,
      title: 'Accuracy',
      description: 'Precise valuations backed by thorough research and analysis',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Committed to delivering exceptional service and results',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sky-600 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {aboutData.headline}
            </h1>
            <p className="text-xl text-sky-50">
              {aboutData.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-metallic-gold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Experience You Can Trust
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {aboutData.experience}
              </p>

              <div className="bg-gradient-to-br from-sky-50 to-green-50 border-l-4 border-sky-600 p-6 rounded-r-lg">
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Our Mission</h3>
                <p className="text-gray-700">{aboutData.mission}</p>
              </div>
            </div>

            {/* Right Column - Credentials */}
            <div>
              <Card className="border-2 border-sky-100 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Credentials & Certifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {aboutData.credentials.map((credential) => (
                      <li key={credential} className="flex items-start gap-3">
                        <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 font-medium">{credential}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <div className="mt-8 grid grid-cols-2 gap-6">
                <div className="bg-white border-2 border-sky-100 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="text-4xl font-bold text-sky-600 mb-2">15,000+</div>
                  <div className="text-gray-600 font-medium">Appraisals Completed</div>
                </div>
                <div className="bg-white border-2 border-green-100 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="text-4xl font-bold text-green-600 mb-2">30+</div>
                  <div className="text-gray-600 font-medium">Years Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-metallic-gold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide our work every day
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <Card key={value.title} className="text-center border-2 border-transparent hover:border-sky-200 hover:shadow-xl transition-all">
                  <CardHeader>
                    <div className="w-16 h-16 bg-gradient-to-br from-sky-600 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;